import apiClient from '../utils/apiClient';
import { WishlistItem, Card, Product, InsertSet } from 'shared/models';

const API_URL = '/api/wishlist';

const wishlistService = {
  // Add all or missing cards from an insert set to wishlist
  addInsertSetToWishlist: async (insertSetId, mode = 'all') => {
    if (!insertSetId) {
      throw new Error('Insert Set ID is required');
    }
    
    // Validate mode
    if (mode !== 'all' && mode !== 'missing') {
      throw new Error('Invalid mode. Must be "all" or "missing"');
    }
    
    const response = await apiClient.post(`${API_URL}/insert-set/${insertSetId}`, { mode });
    return response.data;
  },
  // Get products in the current user's wishlist with pagination
  getWishlistProductsByPage: async (page = 1, limit = 20, sortBy = null) => {
    const params = {
      page,
      limit
    };
    
    // Add optional sort parameter if provided
    if (sortBy) {
      params.sortBy = JSON.stringify(sortBy);
    }
    
    const response = await apiClient.get(`${API_URL}/products`, { 
      params
    });
    
    return {
      products: (response.data.products || []).map(product => new Product(product)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalProducts: response.data.totalProducts || 0
    };
  },
  
  // Get products in a specific user's wishlist with pagination
  getUserWishlistProductsByPage: async (userId, page = 1, limit = 20, sortBy = null) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    const params = {
      page,
      limit
    };
    
    // Add optional sort parameter if provided
    if (sortBy) {
      params.sortBy = JSON.stringify(sortBy);
    }
    
    const response = await apiClient.get(`${API_URL}/products/user/${userId}`, { 
      params
    });
    
    return {
      products: (response.data.products || []).map(product => new Product(product)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalProducts: response.data.totalProducts || 0
    };
  },
  
  // Get insert sets in a specific user's wishlist for a product with pagination
  getUserWishlistInsertSetsByProduct: async (userId, productId, page = 1, limit = 50) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    if (!productId) {
      throw new Error('Product ID is required');
    }
    
    const params = {
      page,
      limit
    };
    
    const response = await apiClient.get(`${API_URL}/insert-sets/user/${userId}/product/${productId}`, { 
      params
    });
    
    return {
      insertSets: (response.data.insertSets || []).map(data => new InsertSet(data)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalInsertSets: response.data.totalInsertSets || 0
    };
  },
  
  // Get user wishlist items for a specific insert set
  getUserWishlistByInsertSet: async (userId, insertSetId, page = 1, limit = 50) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    if (!insertSetId) {
      throw new Error('Insert Set ID is required');
    }
    
    const params = {
      page,
      limit
    };
    
    const response = await apiClient.get(`${API_URL}/cards/user/${userId}/insert-set/${insertSetId}`, { 
      params
    });
    
    return {
      wishlistItems: (response.data.wishlistItems || []).map(item => new WishlistItem(item)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalItems: response.data.totalItems || 0
    };
  },
  
  // Get public wishlist of a user with pagination
  getPublicUserWishlistByPage: async (userId, page = 1, limit = 20) => {
    const params = {
      page,
      limit
    };
    
    const response = await apiClient.get(`${API_URL}/user/${userId}/public`, { params });
    
    return {
      wishlistItems: (response.data.wishlistItems || []).map(item => new WishlistItem(item)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalItems: response.data.totalItems || 0
    };
  },
  
  // Get wishlist item for a specific card
  getUserWishlistItemByCard: async (cardId) => {
    if (!cardId) {
      throw new Error('Card ID is required');
    }
    
    // Direct API call to card-specific endpoint
    try {
      const response = await apiClient.get(`${API_URL}/card/${cardId}`);
      
      // If card is in wishlist, return the wishlist item
      if (response.data.wishlistItem) {
        return new WishlistItem(response.data.wishlistItem);
      }
      
      // If card is not in wishlist, return null
      return null;
    } catch (error) {
      // If the error is a 404, it means the card is not in the wishlist
      if (error.response && error.response.status === 404) {
        return null;
      }
      
      // For other errors, rethrow
      throw error;
    }
  },
  
  // Add a card to wishlist - simplified API
  addToWishlist: async (card) => {
    if (!card || !card.id) {
      throw new Error('Invalid card provided');
    }
    
    if (!card.productId && !card.product) {
      throw new Error('Product ID is required for wishlist items');
    }
    
    if (!card.insertSetId && !card.insertSet) {
      throw new Error('Insert Set ID is required for wishlist items');
    }
    
    // Direct API call with required data
    const response = await apiClient.post(API_URL, {
      card: card.id,
      product: card.productId || (card.product && (card.product.id || card.product._id)),
      insertSet: card.insertSetId || (card.insertSet && (card.insertSet.id || card.insertSet._id)),
      priority: 5 // Default priority
    });
    
    return response.data;
  },
  
  // Remove a card from wishlist - simplified API
  removeFromWishlist: async (card) => {
    if (!card || !card.id) {
      throw new Error('Invalid card provided');
    }
    
    // Direct API call to card-based endpoint
    const response = await apiClient.delete(`${API_URL}/card/${card.id}`);
    return response.data;
  },
  
  // Get wishlist statistics for a user
  getUserWishlistStats: async (userId) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    const response = await apiClient.get(`${API_URL}/stats/user/${userId}`);
    
    return {
      totalCards: response.data.totalCards || 0,
      totalProducts: response.data.totalProducts || 0,
      totalInsertSets: response.data.totalInsertSets || 0
    };
  }
};

export default wishlistService;